import React from 'react';
import { Image } from 'antd';
import { ProductImagesPayload } from '../bot-core/types/chat.types';

interface ProductImageGalleryProps {
  data: ProductImagesPayload;
}

/**
 * In-chat photo gallery rendered when the bot ran show_product_images. Shows the
 * hotel main image followed by the SELECTED room's photos as a horizontal strip;
 * tapping any tile opens an antd lightbox (PreviewGroup) to browse them full-size.
 *
 * Captions mirror the WhatsApp delivery labels: 🏨 for the hotel, 🛏 for the
 * room (only the first room tile is captioned to avoid repetition).
 */
const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({ data }) => {
  const tiles: Array<{ url: string; caption?: string }> = [];

  if (data.hotel?.image) {
    tiles.push({
      url: data.hotel.image,
      caption: `🏨 ${data.hotel.name ?? 'Hotel'}`,
    });
  }

  const roomImages = data.room?.images ?? [];
  roomImages.forEach((url, i) => {
    tiles.push({
      url,
      caption: i === 0 ? `🛏 ${data.room?.name ?? 'Your room'}` : undefined,
    });
  });

  if (tiles.length === 0) return null;

  return (
    <div className="bot-image-gallery">
      {/* The lightbox portals to <body> at antd's z-index (1080), under the
          chat drawer (9998). rootClassName tags both of its portals — the
          image dialog and the zoom/close toolbar — so bot.less can lift them. */}
      <Image.PreviewGroup preview={{ rootClassName: 'bot-image-preview' }}>
        <div className="bot-image-gallery__strip">
          {tiles.map((tile, i) => (
            <figure className="bot-image-gallery__tile" key={`${tile.url}-${i}`}>
              <Image
                src={tile.url}
                alt={tile.caption ?? 'photo'}
                className="bot-image-gallery__img"
                rootClassName="bot-image-gallery__img-root"
              />
              {tile.caption && (
                <figcaption className="bot-image-gallery__caption">
                  {tile.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </Image.PreviewGroup>
    </div>
  );
};

export default ProductImageGallery;
